import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { ImportProgressDTO } from '../../global';
import type { ToastKind } from '../../components/Toast';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { Input, Label } from '../../components/ui/Input';
import { useLanguage } from '../../lib/i18n';

interface ImportSectionProps {
  onNotify: (kind: ToastKind, text: string) => void;
}

/**
 * Ayarlar > İçe aktar. Şimdilik Granola: kullanıcı Granola'da oluşturduğu
 * kişisel API anahtarını yapıştırıyor, notlar başlık, özet ve transkriptiyle,
 * özgün tarihinde toplantı olarak geliyor (services/imports/granola.ts).
 *
 * Anahtar yalnızca bu çağrıda kullanılıyor: ne burada ne ana süreçte
 * saklanıyor; aktarım bitince (başarılı ya da değil) kutu boşaltılıyor.
 */
export const ImportSection: React.FC<ImportSectionProps> = ({ onNotify }) => {
  const { t } = useLanguage();
  const [apiKey, setApiKey] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [progress, setProgress] = useState<ImportProgressDTO | null>(null);

  useEffect(() => window.zabit.onImportProgress(setProgress), []);

  const startImport = async () => {
    const key = apiKey.trim();
    if (!key || isImporting) return;
    setIsImporting(true);
    setProgress(null);
    try {
      const result = await window.zabit.importFromGranola(key);
      if (result.imported === 0 && result.failed === 0) {
        onNotify(
          'info',
          result.skipped > 0
            ? t('Granola’daki notların hepsi zaten aktarılmış.', 'All your Granola notes were already imported.')
            : t('Granola’da aktarılacak not bulunamadı.', 'No notes were found in Granola to import.')
        );
      } else {
        const parts = [
          t(`${result.imported} not aktarıldı`, `${result.imported} notes imported`),
          ...(result.skipped > 0 ? [t(`${result.skipped} not zaten vardı`, `${result.skipped} already there`)] : []),
          ...(result.failed > 0 ? [t(`${result.failed} not aktarılamadı`, `${result.failed} could not be imported`)] : []),
        ];
        onNotify(result.failed > 0 && result.imported === 0 ? 'error' : 'success', `${parts.join(' · ')}.`);
      }
    } catch (err) {
      onNotify('error', (err as Error).message || t('Granola’dan aktarılamadı.', 'Could not import from Granola.'));
    } finally {
      // Anahtar ekranda da kalmasın.
      setApiKey('');
      setIsImporting(false);
      setProgress(null);
    }
  };

  const progressText = !progress
    ? t('Bağlanılıyor…', 'Connecting…')
    : progress.phase === 'listing'
      ? t(`Notlar okunuyor… (${progress.done})`, `Reading notes… (${progress.done})`)
      : t(`Aktarılıyor… ${progress.done} / ${progress.total}`, `Importing… ${progress.done} / ${progress.total}`);

  return (
    <div className="space-y-3">
      <Label htmlFor="granola-api-key" className="text-muted-foreground">
        Granola
      </Label>
      <Card>
        <CardContent className="space-y-3">
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              void startImport();
            }}
          >
            <Input
              id="granola-api-key"
              type="password"
              value={apiKey}
              disabled={isImporting}
              placeholder="grn_…"
              autoComplete="off"
              spellCheck={false}
              maxLength={300}
              onChange={(event) => setApiKey(event.target.value)}
              aria-label={t('Granola API anahtarı', 'Granola API key')}
            />
            <div className="flex items-center gap-3">
              <Button type="submit" variant="secondary" disabled={!apiKey.trim() || isImporting}>
                {isImporting && <Loader2 className="size-4 animate-spin" />}
                {t('Granola’dan içe aktar', 'Import from Granola')}
              </Button>
              {isImporting && (
                <span className="text-[0.8rem] tabular-nums text-muted-foreground" aria-live="polite">
                  {progressText}
                </span>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <p className="text-[0.8rem] leading-relaxed text-muted-foreground">
        {t(
          'Granola → Ayarlar → Connectors → Personal API Keys’ten bir API anahtarı oluşturup yapıştır. Anahtarın yalnızca bir kez kullanılır ve saklanmaz. Daha önce aktarılan notlar atlanır.',
          'Paste an API key from Granola → Settings → Connectors → Personal API Keys. Your key is used once and never stored. Notes you already imported are skipped.'
        )}
      </p>
    </div>
  );
};
